import { ComponentRef, Directive, DoCheck, Input, OnChanges, OnDestroy, SimpleChanges, Type, ViewContainerRef, inject } from '@angular/core';
import { Subscription } from 'rxjs';

export interface StepConfig {
  load: () => Promise<Type<unknown>>;
  inputs?: () => Record<string, unknown>;
  outputs?: Record<string, (event: any) => void>;
  hostClass?: string;
}

@Directive({
  selector: '[appStepOutlet]',
  standalone: true,
})
export class StepOutletDirective implements OnChanges, DoCheck, OnDestroy {
  @Input({ required: true }) appStepOutlet: StepConfig | undefined;
  private vcr = inject(ViewContainerRef);
  private componentRef?: ComponentRef<unknown>;
  private subscriptions: Subscription[] = [];
  private loadId = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appStepOutlet']) {
      this.render(this.appStepOutlet);
    }
  }

  ngDoCheck(): void {
    this.applyInputs();
  }

  ngOnDestroy(): void {
    this.loadId++;
    this.clear();
  }

  private async render(config: StepConfig | undefined): Promise<void> {
    const id = ++this.loadId;
    this.clear();
    if (!config) {
      return;
    }
    // Un reintento por si falla la descarga del chunk (red inestable en el webview)
    const component = await config.load().catch(() => config.load());
    if (id !== this.loadId) {
      return;
    }
    this.componentRef = this.vcr.createComponent(component);
    if (config.hostClass) {
      this.componentRef.location.nativeElement.classList.add(...config.hostClass.split(' '));
    }
    for (const [name, handler] of Object.entries(config.outputs ?? {})) {
      const output = (this.componentRef.instance as Record<string, any>)[name];
      this.subscriptions.push(output.subscribe(handler));
    }
    this.applyInputs();
  }

  private applyInputs(): void {
    if (!this.componentRef || !this.appStepOutlet?.inputs) {
      return;
    }
    for (const [name, value] of Object.entries(this.appStepOutlet.inputs())) {
      this.componentRef.setInput(name, value);
    }
  }

  private clear(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];
    this.componentRef = undefined;
    this.vcr.clear();
  }
}
